import { getMyProfile, getToken, consumeRetailerTokens } from './apiService';

const KEYS = {
  purchased:    'walkin_shop_tokens_purchased',
  available:    'walkin_shop_tokens_available',
  used:         'walkin_shop_tokens_used',
  deals:        'walkin_shop_deals',
  profile:      'walkin_shop_profile',
  codes:        'walkin_qr_codes',
  notifPerm:    'walkin_notif_permission',
  notifDeals:   'walkin_notif_deals',
  subscription: 'walkin_shop_subscription', // 'tokens' | 'unlimited'
};

export const STATUS = {
  ACTIVE:    'actief',
  PAUSED:    'gepauzeerd',
  EXPIRED:   'verlopen',
  DELETED:   'verwijderd',
  SCHEDULED: 'gepland',
};

// Schedule helpers ---------------------------------------------------------
// Profile may store opening hours under either key:
//   - openingHours[ma|di|wo|do|vr|za|zo]   (Dutch — preferred)
//   - hours[mon|tue|wed|thu|fri|sat|sun]   (legacy from signup form)
const DUTCH_DAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const ENG_DAYS   = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function getHoursForDate(profile, date) {
  if (!profile) return null;
  const idx = date.getDay();
  const hours = profile.openingHours || profile.hours || {};
  return hours[DUTCH_DAYS[idx]] || hours[ENG_DAYS[idx]] || null;
}

export function isShopClosedOn(profile, date) {
  const h = getHoursForDate(profile, date);
  if (!h) return false; // no profile info → don't block
  return !!h.closed;
}

export function isWithinOpeningHours(profile, date, hhmm) {
  const h = getHoursForDate(profile, date);
  if (!h || h.closed) return false;
  return hhmm >= h.open && hhmm <= h.close;
}

export function autoPromoteScheduledDeals() {
  const now = Date.now();
  let touched = false;
  let all = [];
  try { all = JSON.parse(localStorage.getItem(KEYS.deals) || '[]'); } catch { all = []; }
  const next = all.map((d) => {
    if (d.status === STATUS.SCHEDULED && d.validFrom && new Date(d.validFrom).getTime() <= now) {
      touched = true;
      return { ...d, status: STATUS.ACTIVE };
    }
    return d;
  });
  if (touched) {
    localStorage.setItem(KEYS.deals, JSON.stringify(next));
    window.dispatchEvent(new Event('walkin-deals-change'));
  }
  return next;
}

const readNum = (key, fallback) => {
  const raw = sessionStorage.getItem(key);
  if (raw === null || raw === undefined || raw === '') return fallback;
  const n = parseInt(raw, 10);
  return isNaN(n) ? fallback : n;
};

// Mirror token/subscription session writes to the canonical localStorage
// shop record (both keys) so admin/dashboard/new-deal/checkout all stay in
// sync on the same browser. Match by email since the same shop may have
// different ids across the two keys.
function syncShopFieldsToLocalStorage(patch) {
  const email = (sessionStorage.getItem('walkin_shop_email') || '').toLowerCase();
  if (!email) return;
  let touched = false;
  for (const key of ['walkin_shops', 'walkin_admin_shops']) {
    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch { list = []; }
    let keyTouched = false;
    const next = list.map((s) => {
      if ((s.email || '').toLowerCase() !== email) return s;
      keyTouched = true;
      return { ...s, ...patch };
    });
    if (keyTouched) {
      localStorage.setItem(key, JSON.stringify(next));
      touched = true;
    }
  }
  if (touched) window.dispatchEvent(new Event('walkin-admin-shops-change'));
}

// Pulls the wallet in from the backend (GET /retailers/me) and leaves it in
// sessionStorage, which is where the synchronous getTokenBalance() reads.
//
// Deliberately NOT folded into getTokenBalance itself. That function is
// synchronous and is called inside consumeToken/consumeTokens/
// addPurchasedTokens and in half a dozen useState initialisers, none of
// which can await. Making it async would not fail loudly there — a pending
// promise reads as `available: undefined`, `undefined <= 0` is false, and
// the arithmetic downstream writes NaN into the wallet.
//
// Returns the balance it wrote, or null when there is nothing to read: no
// retailer session, backend unreachable, or a response without the counters.
// In every one of those cases whatever the session already holds is left
// alone, so a failed refresh degrades to the previous number rather than to
// zero.
export async function refreshTokenBalance() {
  if (!getToken()) return null;

  let profile;
  try {
    profile = await getMyProfile();
  } catch {
    return null;
  }

  // Plan first: Unlimited lives in the database since backend 0.3. When the
  // backend says nothing about it (older build) the browser value stays.
  applyPlanFromBackend(profile);

  const purchased = Number(profile?.tokensPurchased);
  const used      = Number(profile?.tokensUsed);
  if (!Number.isFinite(purchased) || !Number.isFinite(used)) return null;

  // The backend computes this, but derive it if an older build did not send
  // it — the two must never disagree.
  const fromApi = Number(profile?.tokensAvailable);
  const available = Number.isFinite(fromApi) ? fromApi : purchased - used;

  const changed =
    sessionStorage.getItem(KEYS.purchased) !== String(purchased) ||
    sessionStorage.getItem(KEYS.available) !== String(available) ||
    sessionStorage.getItem(KEYS.used)      !== String(used);

  sessionStorage.setItem(KEYS.purchased, String(purchased));
  sessionStorage.setItem(KEYS.available, String(available));
  sessionStorage.setItem(KEYS.used,      String(used));

  // Only when something actually moved. The token screens re-read on this
  // event, and several of them also call this function — an unconditional
  // dispatch would loop between the two.
  if (changed) window.dispatchEvent(new Event('walkin-token-change'));

  return { purchased, available, used, unlimited: false };
}

// Mirrors the plan the backend reports (GET /retailers/me, purchase and
// cancel responses) into the session keys every shop screen reads. Returns
// true when the backend reported a plan at all.
export function applyPlanFromBackend(r) {
  if (!r || r.isUnlimited === undefined) return false;
  const before = [
    sessionStorage.getItem(KEYS.subscription),
    sessionStorage.getItem(SUB_KEYS.plan),
    sessionStorage.getItem(SUB_KEYS.endDate),
    sessionStorage.getItem(SUB_KEYS.cancelledAt),
  ].join('|');

  const set = (key, value) => {
    if (value === null || value === undefined || value === '') sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, String(value));
  };
  set(KEYS.subscription, r.isUnlimited ? 'unlimited' : 'tokens');
  set(SUB_KEYS.plan, r.isUnlimited ? 'unlimited' : (r.fallbackPlan || r.subscriptionPlan || 'starter'));
  set(SUB_KEYS.startDate, r.subscriptionStartedAt);
  set(SUB_KEYS.cancelledAt, r.isUnlimited ? r.subscriptionCancelledAt : null);
  set(SUB_KEYS.endDate, r.isUnlimited ? r.subscriptionEndsAt : null);
  set(SUB_KEYS.fallback, r.fallbackPlan || 'starter');
  // The 0.2 code wrote 'unlimited' into the counters; numbers come back from
  // the backend wallet, so clear those markers.
  if (!r.isUnlimited) {
    if (sessionStorage.getItem(KEYS.available) === 'unlimited') sessionStorage.removeItem(KEYS.available);
    if (sessionStorage.getItem(KEYS.purchased) === 'unlimited') sessionStorage.removeItem(KEYS.purchased);
  }

  const after = [
    sessionStorage.getItem(KEYS.subscription),
    sessionStorage.getItem(SUB_KEYS.plan),
    sessionStorage.getItem(SUB_KEYS.endDate),
    sessionStorage.getItem(SUB_KEYS.cancelledAt),
  ].join('|');
  if (before !== after) window.dispatchEvent(new Event('walkin-token-change'));
  return true;
}

export function getSubscription() {
  return sessionStorage.getItem(KEYS.subscription) || 'tokens';
}

export function setSubscription(type) {
  sessionStorage.setItem(KEYS.subscription, type);
  syncShopFieldsToLocalStorage({ subscription: type });
  window.dispatchEvent(new Event('walkin-token-change'));
}

export function getTokenBalance() {
  const sub = getSubscription();
  if (sub === 'unlimited') {
    return { purchased: 'unlimited', available: 'unlimited', used: 0, unlimited: true };
  }
  const rawAvail = sessionStorage.getItem(KEYS.available);
  if (rawAvail === 'unlimited') {
    return { purchased: 'unlimited', available: 'unlimited', used: 0, unlimited: true };
  }

  let purchased = readNum(KEYS.purchased, null);
  let available = readNum(KEYS.available, null);
  let used      = readNum(KEYS.used,      null);
  if (purchased === null) {
    purchased = 3; available = 3; used = 0;
    sessionStorage.setItem(KEYS.purchased, String(purchased));
    sessionStorage.setItem(KEYS.available, String(available));
    sessionStorage.setItem(KEYS.used,      String(used));
  }
  return { purchased, available, used, unlimited: false };
}

export function setTokenBalance({ purchased, available, used }) {
  const patch = {};
  if (purchased !== undefined) {
    sessionStorage.setItem(KEYS.purchased, String(purchased));
    patch.tokensPurchased = purchased === 'unlimited' ? 'unlimited' : Number(purchased);
  }
  if (available !== undefined) {
    sessionStorage.setItem(KEYS.available, String(available));
    patch.tokensAvailable = available === 'unlimited' ? 'unlimited' : Number(available);
  }
  if (used !== undefined) {
    sessionStorage.setItem(KEYS.used,      String(used));
    patch.tokensUsed = Number(used);
  }
  syncShopFieldsToLocalStorage(patch);
  window.dispatchEvent(new Event('walkin-token-change'));
}

export function consumeToken() {
  const b = getTokenBalance();
  if (b.unlimited) return b; // unlimited bypasses
  if (b.available <= 0) return null;
  const next = { ...b, available: b.available - 1, used: b.used + 1 };
  setTokenBalance(next);
  return next;
}

// A multi-day deal costs a token per day, so the deduction has to happen in
// one go — charging day by day could leave a deal half paid for.
// Spends through the backend ledger rather than decrementing the browser
// copy. Async as a result — callers must await it. consumeToken() (singular)
// is deliberately left synchronous and local; see the note on it above.
//
// The balance check lives in the backend's UPDATE, so the old local
// `available < n` guard is gone: it could only ever be a guess about a number
// another tab may already have spent.
export async function consumeTokens(n) {
  const b = getTokenBalance();
  if (b.unlimited) return b; // unlimited never touches the ledger

  let res;
  try {
    res = await consumeRetailerTokens(n);
  } catch (err) {
    // 400 is the ledger refusing: not enough tokens, which is the `null` the
    // callers already handle. Anything else — offline, 401, 500 — is not a
    // balance problem and must not be shown as one, so it propagates.
    if (err?.status === 400) return null;
    throw err;
  }

  const next = {
    purchased: Number(res.tokensPurchased),
    available: Number(res.tokensAvailable),
    used: Number(res.tokensUsed),
  };
  setTokenBalance(next);
  return { ...next, unlimited: false };
}

export function addPurchasedTokens(amount) {
  const b = getTokenBalance();
  if (b.unlimited) return b;
  const next = {
    purchased: (b.purchased || 0) + amount,
    available: (b.available || 0) + amount,
    used:      b.used || 0,
  };
  setTokenBalance(next);
  return next;
}

// ── Subscription plan + cancellation ─────────────────────────────────────
// The backend has no subscription columns and no subscription endpoints, so
// these fields live in the same place the rest of the wallet already does:
// sessionStorage, mirrored onto the localStorage shop record so they survive a
// new tab and are readable by the admin screens. Moving them server-side needs
// subscription_plan / subscription_start_date / subscription_cancelled_at /
// subscription_end_date / fallback_plan on the retailer table.

export const PLANS = { STARTER: 'starter', GROWTH: 'growth', UNLIMITED: 'unlimited' };

const SUB_KEYS = {
  plan:        'walkin_shop_subscription_plan',
  startDate:   'walkin_shop_subscription_start',
  cancelledAt: 'walkin_shop_subscription_cancelled_at',
  endDate:     'walkin_shop_subscription_end',
  fallback:    'walkin_shop_fallback_plan',
};

const iso = (d) => d.toISOString();

// Last moment of the calendar month a cancellation was requested in: the
// subscription stays active until then.
export function endOfBillingMonth(from = new Date()) {
  const d = new Date(from);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function readSub(key) {
  return sessionStorage.getItem(key) || null;
}

export function getSubscriptionInfo() {
  const stored = readSub(SUB_KEYS.plan);
  // Fall back to the legacy flag so accounts that predate these fields still
  // report the right plan.
  const plan = stored
    || (getSubscription() === 'unlimited' ? PLANS.UNLIMITED : PLANS.STARTER);
  const cancelledAt = readSub(SUB_KEYS.cancelledAt);
  return {
    plan,
    isUnlimited: plan === PLANS.UNLIMITED,
    startDate: readSub(SUB_KEYS.startDate),
    cancelledAt,
    isCancelled: !!cancelledAt,
    endDate: readSub(SUB_KEYS.endDate),
    fallbackPlan: readSub(SUB_KEYS.fallback) || PLANS.STARTER,
  };
}

function writeSub(fields) {
  const patch = {};
  for (const [key, value] of Object.entries(fields)) {
    const storageKey = SUB_KEYS[key];
    if (!storageKey) continue;
    if (value === null) sessionStorage.removeItem(storageKey);
    else sessionStorage.setItem(storageKey, String(value));
    // Mirrored under snake_case so the admin view and any future backend
    // migration read the same names.
    patch[{
      plan: 'subscription_plan',
      startDate: 'subscription_start_date',
      cancelledAt: 'subscription_cancelled_at',
      endDate: 'subscription_end_date',
      fallback: 'fallback_plan',
    }[key]] = value;
  }
  syncShopFieldsToLocalStorage(patch);
  window.dispatchEvent(new Event('walkin-token-change'));
}

// Called when a token package is bought, so the plan to fall back to after
// Unlimited ends reflects what the shop was actually on.
export function setTokenPlan(plan) {
  const next = plan === PLANS.GROWTH ? PLANS.GROWTH : PLANS.STARTER;
  writeSub({
    plan: next,
    startDate: iso(new Date()),
    cancelledAt: null,
    endDate: null,
    fallback: next,
  });
}

export function setUnlimited() {
  sessionStorage.setItem(KEYS.subscription, 'unlimited');
  sessionStorage.setItem(KEYS.purchased, 'unlimited');
  sessionStorage.setItem(KEYS.available, 'unlimited');
  syncShopFieldsToLocalStorage({
    subscription: 'unlimited',
    tokensAvailable: 'unlimited',
    tokensPurchased: 'unlimited',
  });

  // Remember the token plan to revert to when Unlimited ends. An account that
  // was never on a token plan falls back to Starter.
  const previous = getSubscriptionInfo();
  const fallback = previous.plan === PLANS.GROWTH ? PLANS.GROWTH : PLANS.STARTER;
  writeSub({
    plan: PLANS.UNLIMITED,
    startDate: iso(new Date()),
    cancelledAt: null,
    endDate: null,
    fallback,
  });
}

// Retailer-initiated cancellation. The subscription runs to the end of the
// current calendar month, then reverts to the token plan.
export function cancelUnlimited(now = new Date()) {
  const info = getSubscriptionInfo();
  if (!info.isUnlimited || info.isCancelled) return info;

  const end = endOfBillingMonth(now);
  writeSub({
    cancelledAt: iso(now),
    endDate: iso(end),
    fallback: info.fallbackPlan || PLANS.STARTER,
  });
  return getSubscriptionInfo();
}

// Applied before a deal is published: once the paid period is over, drop the
// shop back onto its token plan so deals start costing tokens again.
export function applySubscriptionFallbackIfDue(now = new Date()) {
  const info = getSubscriptionInfo();
  if (!info.isUnlimited || !info.isCancelled || !info.endDate) return info;
  if (new Date(info.endDate).getTime() >= now.getTime()) return info;

  const fallback = info.fallbackPlan === PLANS.GROWTH ? PLANS.GROWTH : PLANS.STARTER;

  // Back to the token model: clear the unlimited markers and give the wallet a
  // real number again so consumeToken() starts deducting.
  sessionStorage.setItem(KEYS.subscription, 'tokens');
  if (sessionStorage.getItem(KEYS.available) === 'unlimited') {
    sessionStorage.setItem(KEYS.available, '0');
  }
  if (sessionStorage.getItem(KEYS.purchased) === 'unlimited') {
    sessionStorage.setItem(KEYS.purchased, '0');
  }
  syncShopFieldsToLocalStorage({
    subscription: 'tokens',
    tokensAvailable: readNum(KEYS.available, 0),
    tokensPurchased: readNum(KEYS.purchased, 0),
  });

  writeSub({
    plan: fallback,
    startDate: iso(now),
    cancelledAt: null,
    endDate: null,
    fallback,
  });

  return getSubscriptionInfo();
}

// NOTE: deals are stored in localStorage so they're shared across tabs
// (shop-owner tab → consumer feed tab on the same browser).
export function getDeals() {
  const raw = localStorage.getItem(KEYS.deals);
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
}

export function saveDeals(deals) {
  localStorage.setItem(KEYS.deals, JSON.stringify(deals));
  window.dispatchEvent(new Event('walkin-deals-change'));
}

export function normalizeArea(area) {
  return (area || '').trim().toLowerCase();
}

// === Redemption + view tracking ===========================================

const REVENUE_PER_REDEMPTION = 23;

const todayKey = () => new Date().toISOString().split('T')[0];

const dispatchStorageMirror = (key) => {
  // Browsers don't fire `storage` in the same tab that wrote the value.
  // We synthesize the event so the dashboard / stats page in this tab
  // refresh just like another tab would.
  try {
    window.dispatchEvent(new StorageEvent('storage', { key }));
  } catch {
    // StorageEvent ctor unsupported — fall back to a generic event
    window.dispatchEvent(new Event(`walkin-storage-${key}`));
  }
};

function bumpStats(shopName, mutate) {
  if (!shopName) return;
  const key = `walkin_stats_${shopName}`;
  let stats = {};
  try { stats = JSON.parse(localStorage.getItem(key) || '{}'); } catch { stats = {}; }
  const today = todayKey();
  const day = stats[today] || { redemptions: 0, views: 0, revenue: 0 };
  mutate(day);
  stats[today] = day;
  localStorage.setItem(key, JSON.stringify(stats));
  dispatchStorageMirror(key);
}

export function recordView(deal) {
  if (!deal || !deal.id) return;
  // Bump deal.views
  let all = [];
  try { all = JSON.parse(localStorage.getItem(KEYS.deals) || '[]'); } catch { all = []; }
  let touched = false;
  const next = all.map((d) => {
    if (String(d.id) === String(deal.id)) {
      touched = true;
      return { ...d, views: (d.views || 0) + 1 };
    }
    return d;
  });
  if (touched) {
    localStorage.setItem(KEYS.deals, JSON.stringify(next));
    dispatchStorageMirror(KEYS.deals);
    window.dispatchEvent(new Event('walkin-deals-change'));
  }
  // Daily stats per shop
  const shopName = deal.shopName || deal.createdBy;
  bumpStats(shopName, (day) => { day.views += 1; });
}

export function recordRedemption({ deal, code }) {
  if (!deal || !deal.id) return null;
  const shopName = deal.shopName || deal.createdBy;
  if (!shopName) return null;

  const redemption = {
    id: 'rdm-' + Date.now(),
    code: code || deal.code || '',
    dealId: deal.id,
    dealTitle: deal.title,
    dealEmoji: deal.emoji || '🏷️',
    shopName,
    redeemedAt: new Date().toISOString(),
    amount: deal.price || 0,
  };

  // Per-shop redemption list (cap 100)
  const rdmKey = `walkin_redemptions_${shopName}`;
  let existing = [];
  try { existing = JSON.parse(localStorage.getItem(rdmKey) || '[]'); }
  catch { existing = []; }
  const trimmed = [redemption, ...existing].slice(0, 100);
  localStorage.setItem(rdmKey, JSON.stringify(trimmed));
  dispatchStorageMirror(rdmKey);

  // Bump redemption count + record used code on the deal
  let all = [];
  try { all = JSON.parse(localStorage.getItem(KEYS.deals) || '[]'); } catch { all = []; }
  const next = all.map((d) => {
    if (String(d.id) !== String(deal.id)) return d;
    const usedCodes = Array.isArray(d.usedCodes) ? d.usedCodes : [];
    return {
      ...d,
      redemptions: (d.redemptions || 0) + 1,
      usedCodes: redemption.code ? [...usedCodes, redemption.code] : usedCodes,
    };
  });
  localStorage.setItem(KEYS.deals, JSON.stringify(next));
  dispatchStorageMirror(KEYS.deals);
  window.dispatchEvent(new Event('walkin-deals-change'));

  // Daily stats
  bumpStats(shopName, (day) => {
    day.redemptions += 1;
    day.revenue += REVENUE_PER_REDEMPTION;
  });

  return redemption;
}

// Per-day view/redemption/revenue counters, written by this browser into
// walkin_stats_<shopName>. There is no backend equivalent to read instead:
// the only stats route is GET /retailers/admin/stats, which is admin-only and
// platform-wide. Deals do carry totalViews/totalRedemptions per deal on
// /deals/retailer/my-deals, but that is a running total, not the daily series
// these three functions return. A retailer stats endpoint would be new
// backend work.
export function getTodayStats(shopName) {
  if (!shopName) return { redemptions: 0, views: 0, revenue: 0 };
  try {
    const all = JSON.parse(localStorage.getItem(`walkin_stats_${shopName}`) || '{}');
    return all[todayKey()] || { redemptions: 0, views: 0, revenue: 0 };
  } catch {
    return { redemptions: 0, views: 0, revenue: 0 };
  }
}

export function getRangeStats(shopName, days) {
  const total = { redemptions: 0, views: 0, revenue: 0 };
  if (!shopName) return total;
  let all = {};
  try { all = JSON.parse(localStorage.getItem(`walkin_stats_${shopName}`) || '{}'); }
  catch { all = {}; }
  for (let i = 0; i < days; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const k = d.toISOString().split('T')[0];
    const day = all[k];
    if (day) {
      total.redemptions += day.redemptions || 0;
      total.views += day.views || 0;
      total.revenue += day.revenue || 0;
    }
  }
  return total;
}

export function getDailySeries(shopName, days) {
  // Returns oldest → newest so it renders left → right naturally
  const out = [];
  let all = {};
  try { all = JSON.parse(localStorage.getItem(`walkin_stats_${shopName}`) || '{}'); }
  catch { all = {}; }
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const k = d.toISOString().split('T')[0];
    const day = all[k] || { redemptions: 0, views: 0, revenue: 0 };
    out.push({
      date: d,
      key: k,
      redemptions: day.redemptions || 0,
      views: day.views || 0,
      revenue: day.revenue || 0,
    });
  }
  return out;
}

export function addDeal(deal) {
  const deals = getDeals();
  const next = [deal, ...deals];
  saveDeals(next);
  return next;
}

export function updateDeal(id, patch) {
  const deals = getDeals();
  const next = deals.map((d) => (d.id === id ? { ...d, ...patch } : d));
  saveDeals(next);
  return next;
}

export function removeDeal(id) {
  saveDeals(getDeals().filter((d) => d.id !== id));
}

export function generate6DigitCode() {
  const used = new Set();
  const raw = sessionStorage.getItem(KEYS.codes);
  if (raw) try { JSON.parse(raw).forEach((c) => used.add(c)); } catch { /* noop */ }
  let code;
  do {
    code = String(Math.floor(100000 + Math.random() * 900000));
  } while (used.has(code));
  used.add(code);
  sessionStorage.setItem(KEYS.codes, JSON.stringify(Array.from(used)));
  return code;
}

export function isValidCode(code) {
  const raw = sessionStorage.getItem(KEYS.codes);
  if (!raw) return false;
  try {
    const codes = JSON.parse(raw);
    return Array.isArray(codes) && codes.includes(String(code));
  } catch {
    return false;
  }
}

export function expireDealsIfNeeded() {
  const now = Date.now();
  let changed = false;
  const deals = getDeals().map((d) => {
    if (d.status !== STATUS.EXPIRED && d.expiresAt && new Date(d.expiresAt).getTime() <= now) {
      changed = true;
      return { ...d, status: STATUS.EXPIRED };
    }
    return d;
  });
  if (changed) saveDeals(deals);
  return deals;
}

export function endOfTodayISO() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

export function getProfile() {
  const raw = sessionStorage.getItem(KEYS.profile);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function saveProfile(profile) {
  sessionStorage.setItem(KEYS.profile, JSON.stringify(profile));
}
